import appConfig from 'appConfig';

import { CreateControllerFn } from '@wix/yoshi-flow-editor';
import { createEventHandler, getSettingsValues } from '@wix/tpa-settings';

import { Events, WixCodeMembersDummy } from './types';
import settingsParams from './settingsParams';

const createController: CreateControllerFn = async ({
  controllerConfig,
  appData,
  flowAPI,
}) => {
  const { setProps } = controllerConfig;

  const config = flowAPI.controllerConfig;
  const { withErrorHandler, getResolvedError } = flowAPI.errorHandler;
  const api = config.wixCodeApi;

  let member: Awaited<
    ReturnType<NonNullable<WixCodeMembersDummy['currentMember']>['getMember']>
  >;
  const currentMember = (api as any).members
    ?.currentMember as WixCodeMembersDummy['currentMember'];
  if (currentMember === undefined) {
    member = undefined;
  } else {
    member = await withErrorHandler(() => currentMember.getMember(), {
      errorCodesMap: {
        applicationError: {
          DEADLINE_EXCEEDED: false,
          UNAUTHENTICATED: false,
          PERMISSION_DENIED: false,
          NOT_FOUND: false,
        },
      },
    }).catch((error) => {
      throw getResolvedError(error);
    });
  }

  const avatar = member?.profile?.profilePhoto?.url;

  const navigation = {
    baseUrl: api.location.baseUrl,
    navigateTo: api?.location?.navigateTo,
    navigateToUrl: api?.location?.to,
  };

  const publicData = controllerConfig.config.publicData.COMPONENT || {};
  const handler = createEventHandler<Events>(publicData);
  const onHandlerEvent = handler.on.bind(this);
  const onHandlerReset = handler.onReset.bind(this);

  const initialLayoutOpen = publicData.initialVisualLayoutState === 'visible';

  const instanceToken =
    api?.site?.getAppToken && api.site.getAppToken(appConfig.appDefinitionId);

  const getSettings = (pd: any) => {
    const { currentLanguage, isEnabled, siteLanguages } =
      controllerConfig.wixCodeApi.window.multilingual;

    const currentLanguageSettings = getSettingsValues(pd, settingsParams, {
      t: flowAPI.translations.t,
      language: isEnabled ? currentLanguage : undefined,
    });

    const primaryLanguageSettings = isEnabled
      ? getSettingsValues(pd, settingsParams, {
          t: flowAPI.translations.t,
          language: siteLanguages.find(
            ({ isPrimaryLanguage }) => isPrimaryLanguage,
          )!.languageCode,
        })
      : currentLanguageSettings;

    const settings = {
      current: currentLanguageSettings,
      primary: primaryLanguageSettings,
    };

    return settings;
  };

  api.user.onLogin(async () => {
    const _member = await (
      (api as any).members as WixCodeMembersDummy | undefined
    )?.currentMember?.getMember();

    const _avatar = _member?.profile?.profilePhoto?.url;
    setProps({ avatar: _avatar });
  });

  api.site.onInstanceChanged((e) => {
    // https://github.com/wix-private/wix-ai-assistant-bot/issues/4451
    setProps({
      instanceToken: e.instance,
    });
  }, appConfig.appDefinitionId);

  return {
    async pageReady() {
      setProps({
        onHandlerEvent,
        onHandlerReset,
        navigation,
        initialLayoutOpen,
        instanceToken,
        avatar,
        page: flowAPI.viewerScriptFlowAPI.siteAPI.currentPage,
        settings: getSettings(publicData),
        ecom: (api as any).ecom,
      });
    },
    updateConfig($w, c) {
      const pd = c.publicData.COMPONENT || {};
      handler.notify(pd);
      setProps({
        settings: getSettings(pd),
      });
    },
  };
};

export default createController;
